import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  Mail, X, Send, Loader2, MessageSquare, CheckCircle, Crown, User, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format time ago
const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
};

export const ContactAdminButton = () => {
  const { user, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  
  // Fetch unread count
  useEffect(() => {
    if (token && user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [token, user]);
  
  // Fetch conversation when modal opens
  useEffect(() => {
    if (isOpen && token) {
      fetchConversation();
    }
  }, [isOpen, token]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  };
  
  const fetchConversation = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/messages/my-conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversation(response.data.conversation);
      setMessages(response.data.messages || []);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to fetch conversation');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSend = async () => {
    if (!message.trim()) return;
    
    setSending(true);
    try {
      await axios.post(
        `${API}/messages/send-to-admin`,
        { 
          subject: subject || 'General Inquiry',
          message: message.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Message sent to admin!');
      setMessage('');
      setSubject('');
      fetchConversation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };
  
  // Admin users (by role or is_admin flag) don't see this button - they use the inbox
  if (!user || user.role === 'admin' || user.is_admin === true) {
    return null;
  }
  
  return (
    <>
      {/* Floating Contact Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 left-6 z-[9998] w-12 h-12 rounded-full bg-gradient-to-br from-babylon-amethyst to-babylon-violet flex items-center justify-center shadow-lg shadow-babylon-amethyst/30 hover:shadow-babylon-amethyst/50"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        title="Contact Admin"
      >
        <Mail className="w-5 h-5 text-white" />
        {unreadCount > 0 && (
          <motion.span 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-5 h-5 bg-babylon-blood rounded-full text-[10px] text-white flex items-center justify-center font-bold"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.span>
        )}
      </motion.button>
      
      {/* Message Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-lg bg-gradient-to-br from-babylon-obsidian to-babylon-shadow border border-babylon-gold/20 rounded-xl overflow-hidden shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-babylon-gold/20 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-babylon-amethyst/20 rounded-lg">
                    <Crown className="w-5 h-5 text-babylon-amethyst" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-babylon-gold">Contact Admin</h3>
                    <p className="text-xs text-babylon-text-dim">T3B3TU • Site Creator</p>
                  </div>
                </div>
                <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-babylon-gold/10 rounded-lg">
                  <X size={18} className="text-babylon-text-dim" />
                </button>
              </div>
              
              {/* Messages Area */}
              <div className="h-80">
                {loading ? (
                  <div className="h-full flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-babylon-gold" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center p-6 text-center">
                    <MessageSquare className="w-12 h-12 text-babylon-gold/30 mb-3" />
                    <p className="text-babylon-text-dim text-sm">No messages yet</p>
                    <p className="text-babylon-text-dim text-xs mt-1">Send a message to start a conversation</p>
                  </div>
                ) : (
                  <ScrollArea className="h-full p-4">
                    <div className="space-y-3">
                      {messages.map((msg) => (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[80%] p-3 rounded-xl ${
                            msg.sender_type === 'user' 
                              ? 'bg-babylon-amethyst/20 border border-babylon-amethyst/30' 
                              : 'bg-babylon-gold/10 border border-babylon-gold/20'
                          }`}>
                            {msg.subject && (
                              <p className="text-xs text-babylon-gold font-medium mb-1">{msg.subject}</p>
                            )}
                            <p className="text-sm text-babylon-text whitespace-pre-wrap">{msg.message}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Clock size={10} className="text-babylon-text-dim" />
                              <span className="text-[10px] text-babylon-text-dim">{formatTimeAgo(msg.created_at)}</span>
                              {msg.sender_type === 'admin' && (
                                <span className="text-[10px] text-babylon-gold flex items-center gap-1">
                                  <Crown size={8} /> Admin
                                </span>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>
                )}
              </div>
              
              {/* Compose Area */}
              <div className="p-4 border-t border-babylon-gold/20 space-y-3">
                {messages.length === 0 && (
                  <Input
                    placeholder="Subject (optional)"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="bg-babylon-void border-babylon-gold/20 text-sm"
                    maxLength={200}
                  />
                )}
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Type your message..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="bg-babylon-void border-babylon-gold/20 text-sm min-h-[60px] max-h-[100px] resize-none flex-1"
                    maxLength={2000}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                  />
                  <Button
                    onClick={handleSend}
                    disabled={sending || !message.trim()}
                    className="btn-primary self-end"
                  >
                    {sending ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                  </Button>
                </div>
                <p className="text-[10px] text-babylon-text-dim text-center">
                  Press Enter to send • Shift+Enter for new line
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ContactAdminButton;
